import { Injectable } from '@angular/core';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { CloudFunctionsService, isNetworkError } from 'services/cloud-functions.service';
import { LoggingService } from 'services/logging.service';
import { TelemetryService } from 'services/telemetry.service';
import { UrlQueryService } from 'services/url-query-service';
import { describeError } from '@cf-shared/error-describe';
import { buildInviteLink, ReferralClaimRefusal } from '@cf-shared/data-model/referral';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import {
  ReferralClaimResult,
  ReferralGetOrCreateCodeResult,
  ReferralListFriendsResult,
  ReferralPeekInviteResult,
  ReferralTrainingOutcome,
} from '@cf-shared/referral-interface';
import {
  buildShareMessage,
  provesCallerIsReferred,
  PendingRefStorageProblem,
  rememberPendingRef,
  shouldReportTrainingStarted,
  takePendingRef,
} from 'services/referral';
import { SettingsService } from 'services/settings.service';

/**
 * The referral programme's wiring (#4627).
 *
 * This class is only the wiring. It supplies the real server calls, the real
 * session storage and the real telemetry. Every decision — how an invite is
 * remembered across the sign-up reload, when a stored one is too old to use, how
 * the `ref` is read out of an address, and whether the hub should offer its
 * code-entry box — lives in the plain `services/referral.ts` beside this file,
 * where it is unit-tested without a browser.
 *
 * See design-docs/referral-program.md.
 */
@Injectable({ providedIn: 'root' })
export class ReferralService {
  constructor(
    private cloudFunctions: CloudFunctionsService,
    private logging: LoggingService,
    private urlQuery: UrlQueryService,
    private telemetry: TelemetryService,
    private settings: SettingsService,
  ) {}

  /** The caller's own code and invite link, minted on first use. */
  async getOrCreateCodeAsync(): Promise<ReferralGetOrCreateCodeResult> {
    const result = await this.cloudFunctions.referralGetOrCreateCodeAsync({});
    // This answer carries `hasReferrer`, which is the server saying plainly that
    // somebody invited this person. Taking it costs nothing and repairs the one
    // case the claim-time marker cannot reach: anybody who was already referred
    // before phase 3 shipped, or whose marker was lost. It is not a substitute
    // for the claim-time write — this call only happens when somebody opens the
    // referral hub, and most referred friends never do.
    if (result?.hasReferrer) await this.rememberWasReferredAsync();
    return result;
  }

  /**
   * The friends this caller has brought in, for the hub's list (#4963 phase 3).
   *
   * A failure is left to throw. Unlike the invite page's peek, the caller here is
   * signed in and looking at their own page, and the hub can say plainly that it
   * could not load the list and offer a retry — which is better than an empty list
   * that reads as "you have invited nobody".
   */
  async listFriendsAsync(): Promise<ReferralListFriendsResult> {
    return this.cloudFunctions.referralListFriendsAsync({});
  }

  /**
   * Asks who sent an invite, for the page a friend lands on.
   *
   * A failure is reported as "not valid" rather than propagating, because this
   * runs on a page shown to somebody who is not signed in and has never seen the
   * app before: an error screen is a worse first impression than the plain "this
   * invite is not valid" the page already knows how to show. It is still reported
   * to telemetry, so a broken endpoint does not look like a run of bad links.
   */
  async peekInviteAsync(ref: string): Promise<ReferralPeekInviteResult> {
    try {
      return await this.cloudFunctions.referralPeekInviteAsync({ ref });
    } catch (err) {
      this.reportCallFailure('referral-peek-failed', err);
      return { valid: false };
    }
  }

  /**
   * Attaches the signed-in user to whoever invited them.
   *
   * Returns the refusal when there is one, so a caller can say why. A thrown
   * error is different from a refusal and is left to the caller.
   */
  async claimAsync(ref: string): Promise<ReferralClaimResult> {
    const result = await this.cloudFunctions.referralClaimAsync({ ref });
    // The one choke point every route in goes through: the invite link, the
    // hub's code box and the subscribe page's code box all end up here. That is
    // why the marker is written HERE rather than at each of them.
    if (provesCallerIsReferred(result)) await this.rememberWasReferredAsync();
    return result;
  }

  /**
   * Remembers that this user was invited, so a completed workout may be reported.
   *
   * A save failure is swallowed with a note rather than thrown: the caller is a
   * claim that has already succeeded on the server, and failing it now would tell
   * the friend their invite did not work when it did. The value is still in memory
   * and in this device's stored settings, so the next settings save carries it to
   * the synced document anyway.
   */
  private async rememberWasReferredAsync(): Promise<void> {
    if (this.settings.referralWasReferred) return;
    this.settings.referralWasReferred = true;
    try {
      await this.settings.saveAsync();
    } catch (err) {
      this.reportCallFailure('referral-was-referred-marker-save-failed', err);
    }
  }

  /**
   * Tells the server this user has started training, at most once ever (#4963).
   *
   * Called when a workout is completed. Everything that decides whether to call at
   * all is in `shouldReportTrainingStarted`, in the Angular-free half of this
   * service, so each of its silent refusals is pinned by a test.
   *
   * **It records SUCCESS, never the attempt.** Somebody can create an account,
   * train, and only then type an invite code — so a call correctly refused before
   * their referral existed must leave this armed, or they would sit on "Joined" for
   * ever while they trained. "No referral record" is likewise an ordinary answer
   * and not a fault: it is what a marker and the server disagreeing looks like.
   *
   * Never throws. It runs behind whatever the user is actually doing, at the moment
   * they finish a workout, and nothing here is worth interrupting that for.
   */
  async reportTrainingStartedIfReferredAsync(): Promise<void> {
    if (!shouldReportTrainingStarted({
      wasReferred: this.settings.referralWasReferred,
      alreadyReported: this.settings.referralTrainingReported,
      isManagingClient: this.settings._isManagingClient,
      hasSignedInUser: !!this.telemetry.user?.uid,
      settingsLoaded: this.settings.isLoaded(),
    })) {
      return;
    }

    let outcome: ReferralTrainingOutcome;
    try {
      ({ outcome } = await this.cloudFunctions.referralReportTrainingAsync({}));
    } catch (err) {
      // being offline is by far the commonest way this fails, and the next
      // completed workout tries again — which is exactly why the marker is only
      // written on a recorded answer
      this.reportCallFailure('referral-report-training-failed', err);
      return;
    }

    // NoReferral is deliberately absent from this test. It means the marker and
    // the server's records disagree, which is an ordinary outcome and not a
    // problem to report — and it must NOT disarm the call, because the record may
    // yet be created by somebody typing a code.
    if (outcome !== ReferralTrainingOutcome.Recorded
        && outcome !== ReferralTrainingOutcome.AlreadyRecorded) {
      return;
    }

    this.settings.referralTrainingReported = true;
    try {
      await this.settings.saveAsync();
    } catch (err) {
      // put the marker back so the next completed workout retries. The server has
      // already recorded the flag, so a repeat costs one call answered
      // `already-recorded`.
      this.settings.referralTrainingReported = false;
      this.reportCallFailure('referral-training-marker-save-failed', err);
    }
  }

  /**
   * Reads the invite out of the address the app was opened with.
   *
   * It asks `UrlQueryService` rather than reading `window.location.search`, and
   * that is load-bearing rather than a preference. `UrlQueryService` snapshots
   * the query string once, when the app is constructed. The navigation layer
   * then rebuilds the address from the page stack alone — `UrlService.serialize`
   * produces `/invite` with no query string at all — and calls `replaceState`
   * with it while the app is still booting. So by the time this page's lifecycle
   * hook runs, `window.location.search` is usually already empty, and reading it
   * directly would lose the invite for most visitors while appearing to work
   * whenever the timing happened to go the other way.
   *
   * There is no parsing left to do here. `UrlQueryService` holds the query
   * string in a `URLSearchParams`, which has already undone the percent-encoding
   * and turned an absent parameter into null — so this is a lookup, not a parse.
   * An earlier version of this file parsed the query string a second time in a
   * plain helper; that helper was removed rather than left beside this, because
   * two parsers of one value drift apart and only one of them is ever the one
   * being used.
   */
  readRefFromCurrentUrl(): string | undefined {
    return this.urlQuery.getUrlParam('ref') || undefined;
  }

  /**
   * Where the pending-invite bridge sends what it could not do. Info tier: every case is an
   * expected browser limit or a damaged value already cleared, and the only cost is that the
   * friend types the code on the hub instead (#5191).
   */
  private readonly reportPendingRefProblem = (problem: PendingRefStorageProblem, err: unknown): void => {
    this.logging.trackSilentInfo(problem, { error: describeError(err) });
  };

  /** Remembers an invite so it survives the navigation and reload that sign-up causes. */
  rememberForSignUp(ref: string): void {
    rememberPendingRef(sessionStorage, ref, new Date().toISOString(), this.reportPendingRefProblem);
  }

  /**
   * Claims an invite that was stored before sign-up, if there is one worth claiming.
   *
   * Called once the user is signed in. It reports rather than throws: this runs in
   * the boot sequence behind whatever the user is actually doing, so a failure must
   * not take the app down with it — the friend can still type the code on the hub.
   */
  async claimPendingIfAnyAsync(): Promise<void> {
    const ref = takePendingRef(sessionStorage, new Date().toISOString(), this.reportPendingRefProblem);
    if (!ref) return;

    try {
      const result = await this.claimAsync(ref);
      if (result.claimed) {
        this.telemetry.trackEvent('referral_signup', { referral_source: 'link' });
      } else {
        // a refusal is an ordinary outcome, not a fault — most of them mean the
        // offer simply does not apply to this account
        this.logging.trackSilentInfo('referral-claim-refused', { refusal: result.refusal });
      }
    } catch (err) {
      this.reportCallFailure('referral-claim-failed', err);
    }
  }

  /**
   * Reports a failed server call at the right severity.
   *
   * Public because the referral PAGES report their own failures through it too.
   * That is deliberate rather than lazy: the offline/problem split below is easy
   * to get wrong and expensive when it is, so there is one copy of it and every
   * referral failure goes through it.
   *
   * Being offline is by far the commonest way one of these calls fails, and it is
   * NOT a problem — the tier matters here for a reason that is invisible from this
   * file. A problem-tier report marks the whole session as having gone wrong
   * (`noteSilentProblem` in `services/session-health.ts`), and that is what
   * suppresses the app-rating prompt for the rest of it. The invite page is a
   * stranger's very first page, so filing an ordinary loss of connectivity as a
   * problem would silence the prompt for anybody who opened an invite on a flaky
   * phone. The name carries `(transient network)` because that is the convention
   * that predicate already recognises.
   *
   * describeError rather than `err.message`: this becomes a telemetry property, so
   * it crosses the wire, and CLAUDE.md names that case. A CloudFunctionHttpError
   * carries its status in `httpStatus` and a CouchDB-shaped rejection has no
   * `.message` at all, so reading `.message` would report `undefined` about the
   * failures worth knowing most about.
   */
  reportCallFailure(name: string, err: unknown): void {
    const detail = { detail: describeError(err) };
    if (isNetworkError(err)) {
      this.logging.trackSilentInfo(`${name} (transient network)`, detail);
      return;
    }
    this.logging.trackSilentProblem(name, detail);
  }

  /**
   * The invite link for a code, built against whichever origin this build runs on.
   *
   * It defers to the shared builder rather than assembling the address here: the
   * separator between the code and the link key is deliberately PRIVATE to that
   * module, so that the two halves of a `ref` value can never be written one way
   * and read another. Writing the link by hand would compile perfectly and start
   * producing links the server refuses the day that constant changed.
   *
   * Note the server also returns a ready-made `inviteLink` built from the real app
   * domain, which is the one to prefer wherever it is available.
   */
  buildInviteLinkForCurrentOrigin(code: string, linkKey: string): string {
    return buildInviteLink(getSharedUrlOrigin(), code, linkKey);
  }

  /** The pre-written message, with the link deliberately left out of it. */
  buildShareMessage(text: string): string {
    return buildShareMessage(text);
  }
}

/**
 * Why a code somebody typed was not accepted, in the words we show them.
 *
 * A map rather than a `switch` with a default, on purpose: written this way the
 * compiler refuses the file if a new refusal is ever added to the shared model and
 * nobody wrote wording for it. A `default` arm would silently show "something went
 * wrong" for a refusal we could have explained.
 *
 * It moved here from the hub page when #4962 put the same code-entry box on the
 * SUBSCRIBE page. Two copies of an exhaustive map is exactly the drift the
 * exhaustiveness exists to prevent — the second copy would go on compiling with a
 * stale set of arms. It sits in the SERVICE rather than in `services/referral.ts`
 * because that file is deliberately Angular-free and testable without a browser,
 * and `LL` would break that promise.
 *
 * The hub's wording is reused rather than duplicated: the two boxes refuse for
 * identical reasons, so a second set of strings would be more to translate and to
 * keep in step, for no gain.
 */
export const REFERRAL_REFUSAL_MESSAGES: Record<ReferralClaimRefusal, () => TranslatedString> = {
  [ReferralClaimRefusal.UnknownInvite]: () => LL.referral.hub.wereYouInvited.refusals.unknownInvite(),
  [ReferralClaimRefusal.SelfReferral]: () => LL.referral.hub.wereYouInvited.refusals.selfReferral(),
  [ReferralClaimRefusal.AlreadyReferredByAnother]: () => LL.referral.hub.wereYouInvited.refusals.alreadyReferredByAnother(),
  [ReferralClaimRefusal.HasSubscription]: () => LL.referral.hub.wereYouInvited.refusals.hasSubscription(),
  [ReferralClaimRefusal.AccountTooOld]: () => LL.referral.hub.wereYouInvited.refusals.accountTooOld(),
  [ReferralClaimRefusal.InviterGone]: () => LL.referral.hub.wereYouInvited.refusals.inviterGone(),
};
